import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Send, Loader2, User } from "lucide-react";
import { API } from "@/lib/api";

const DEFAULT_THEME = {
  primary_color: "#D95B38",
  position: "bottom-right",
  header_text: "",
  welcome_message: "",
  launcher_label: "",
  brand_name: "",
  logo_b64: "",
};

function getOrCreateContactId(token) {
  const key = `agentreply_contact_${token}`;
  // Backward compatibility: migrate from the legacy "voxa_*" prefix if present
  const legacyKey = `voxa_contact_${token}`;
  const legacy = localStorage.getItem(legacyKey);
  if (legacy && !localStorage.getItem(key)) {
    localStorage.setItem(key, legacy);
    localStorage.removeItem(legacyKey);
  }
  let cid = localStorage.getItem(key);
  if (!cid) {
    cid = `web_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
    localStorage.setItem(key, cid);
  }
  return cid;
}

const getStored = (token, suffix) => {
  // Migrate legacy voxa_<suffix>_<token> → agentreply_<suffix>_<token>
  const newKey = `agentreply_${suffix}_${token}`;
  const oldKey = `voxa_${suffix}_${token}`;
  const legacy = localStorage.getItem(oldKey);
  if (legacy && !localStorage.getItem(newKey)) {
    localStorage.setItem(newKey, legacy);
    localStorage.removeItem(oldKey);
  }
  return localStorage.getItem(newKey) || null;
};
const setStored = (token, suffix, value) => localStorage.setItem(`agentreply_${suffix}_${token}`, value);

export default function PublicChat() {
  const { token } = useParams();
  const [agent, setAgent] = useState(null);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [needsName, setNeedsName] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [phoneInput, setPhoneInput] = useState("");
  const [displayName, setDisplayName] = useState(null);
  const [contactEmail, setContactEmail] = useState(null);
  const [contactPhone, setContactPhone] = useState(null);
  const contactIdRef = useRef(getOrCreateContactId(token));
  const scrollRef = useRef(null);

  const params = new URLSearchParams(window.location.search);
  const isEmbed = params.get("embed") === "1";

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/public/agents/${token}`);
        setAgent(res.data);
        if (res.data.widget_theme) setTheme({ ...DEFAULT_THEME, ...res.data.widget_theme });
        const stored = getStored(token, "name");
        const e = getStored(token, "email");
        const p = getStored(token, "phone");
        if (e) setContactEmail(e);
        if (p) setContactPhone(p);
        if (stored) {
          setDisplayName(stored);
          // Inject welcome if configured
          const wm = res.data.widget_theme?.welcome_message;
          if (wm) setMessages([{ role: "assistant", text: wm }]);
        } else {
          setNeedsName(true);
        }
      } catch {
        setError("Agent not found.");
      }
    })();
  }, [token]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const submitName = (skip = false) => {
    if (!skip) {
      if (nameInput.trim()) {
        const n = nameInput.trim().slice(0, 60);
        setStored(token, "name", n);
        setDisplayName(n);
      }
      if (emailInput.trim()) {
        const e = emailInput.trim().slice(0, 120);
        setStored(token, "email", e);
        setContactEmail(e);
      }
      if (phoneInput.trim()) {
        const p = phoneInput.trim().slice(0, 40);
        setStored(token, "phone", p);
        setContactPhone(p);
      }
    }
    setNeedsName(false);
    if (theme.welcome_message) {
      setMessages([{ role: "assistant", text: theme.welcome_message }]);
    }
  };

  const send = async () => {
    if (!text.trim() || sending) return;
    const userText = text.trim();
    if (userText.length > 2000) {
      setMessages((m) => [...m, { role: "user", text: userText }, { role: "assistant", text: "Message too long — keep it under 2000 characters.", error: true }]);
      setText("");
      return;
    }
    setText("");
    setMessages((m) => [...m, { role: "user", text: userText }]);
    setSending(true);
    try {
      const res = await axios.post(`${API}/public/agents/${token}/chat`, {
        text: userText,
        session_id: contactIdRef.current,
        display_name: displayName || undefined,
        email: contactEmail || undefined,
        phone: contactPhone || undefined,
      });
      setMessages((m) => [...m, { role: "assistant", text: res.data.reply }]);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg = "Sorry, something went wrong.";
      if (status === 402 && detail) msg = detail;
      else if (status === 429 && detail) msg = detail;
      else if (status === 400 && detail) msg = detail;
      setMessages((m) => [...m, { role: "assistant", text: msg, error: true }]);
    } finally {
      setSending(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F0D0C] text-[#F5F2EB] font-mono">
        {error}
      </div>
    );
  }

  const headerText = theme.header_text || agent?.name || "…";
  const brandName = theme.brand_name || "AgentReply";

  return (
    <div
      data-testid="public-chat-root"
      style={{ "--accent": theme.primary_color }}
      className={`${isEmbed ? "h-screen" : "min-h-screen"} bg-[#0F0D0C] text-[#F5F2EB] flex flex-col`}
    >
      {!isEmbed && (
        <header className="border-b border-[#F5F2EB]/10 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span style={{ background: theme.primary_color }} className="w-2 h-2 inline-block" />
            <span className="font-serif italic text-xl">AgentReply</span>
          </div>
          <a href="/" className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] hover:text-[#F5F2EB]">
            Build your own →
          </a>
        </header>
      )}

      <div className="flex-1 flex items-stretch justify-center w-full">
        <div className={`flex flex-col w-full ${isEmbed ? "" : "max-w-2xl"} ${isEmbed ? "" : "border-x border-[#F5F2EB]/10"}`}>
          <div className="p-4 border-b border-[#F5F2EB]/10 bg-[#1A1715] flex items-center gap-3">
            {theme.logo_b64 ? (
              <img
                src={theme.logo_b64}
                alt=""
                data-testid="chat-header-logo"
                className="w-10 h-10 object-cover rounded-sm"
                style={{ borderColor: theme.primary_color + "55", borderWidth: 1 }}
              />
            ) : (
              <div
                style={{ background: theme.primary_color + "33", color: theme.primary_color }}
                className="w-10 h-10 flex items-center justify-center font-serif text-lg"
              >
                {headerText[0]?.toUpperCase() || "·"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-mono text-sm truncate" data-testid="chat-header-text">{headerText}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full inline-block" />
                online {displayName && <span className="ml-2" style={{ color: theme.primary_color }}>· chatting as {displayName}</span>}
              </div>
            </div>
          </div>

          {needsName && (
            <div className="bg-[#1A1715] border-b border-[#F5F2EB]/10 p-6 fade-up" data-testid="name-prompt">
              <div className="flex items-start gap-3">
                <User className="w-5 h-5 mt-1" style={{ color: theme.primary_color }} strokeWidth={1.5} />
                <div className="flex-1">
                  <div className="font-serif text-lg mb-1">First time here?</div>
                  <div className="font-mono text-xs text-[#A39A8F] mb-4">
                    {headerText} can remember you. Share a few details (all optional).
                  </div>
                  <div className="space-y-2">
                    <input value={nameInput} onChange={(e) => setNameInput(e.target.value)} placeholder="Your name" data-testid="name-input" maxLength={60}
                      className="w-full bg-transparent border-b border-[#F5F2EB]/20 font-mono text-sm px-2 py-2 focus:outline-none" style={{ borderBottomColor: undefined }} />
                    <input value={emailInput} onChange={(e) => setEmailInput(e.target.value)} placeholder="Email (so they can follow up)" type="email" data-testid="email-input" maxLength={120}
                      className="w-full bg-transparent border-b border-[#F5F2EB]/20 font-mono text-sm px-2 py-2 focus:outline-none" />
                    <input value={phoneInput} onChange={(e) => setPhoneInput(e.target.value)} placeholder="Phone or WhatsApp" type="tel" data-testid="phone-input" maxLength={40}
                      className="w-full bg-transparent border-b border-[#F5F2EB]/20 font-mono text-sm px-2 py-2 focus:outline-none" />
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button onClick={() => submitName(false)} data-testid="name-submit"
                      style={{ background: theme.primary_color }}
                      className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#F5F2EB] px-4 py-2 btn-press"
                    >
                      Start chatting
                    </button>
                    <button onClick={() => submitName(true)} data-testid="name-skip"
                      className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] hover:text-[#F5F2EB] px-2"
                    >
                      Skip — stay anonymous
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-3 flex flex-col" data-testid="public-messages">
            {messages.length === 0 && !needsName && (
              <div className="m-auto text-center font-mono text-xs uppercase tracking-[0.25em] text-[#A39A8F]">
                Say hello to {headerText} ↓
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={m.error ? "chat-error-bubble" : undefined}
                  style={m.role === "user" ? { background: theme.primary_color } : undefined}
                  className={`max-w-[80%] p-3 font-mono text-sm shadow-sm ${
                    m.role === "user"
                      ? "text-[#F5F2EB] rounded-l-md rounded-tr-md"
                      : m.error
                        ? "bg-[#2B2624] border border-[#8F2C24]/60 text-[#E0D8CA] rounded-r-md rounded-tl-md"
                        : "bg-[#1A1715] border border-[#F5F2EB]/10 text-[#F5F2EB] rounded-r-md rounded-tl-md"
                  }`}
                >
                  <div className="whitespace-pre-wrap">{m.text}</div>
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-3 rounded-r-md rounded-tl-md">
                  <span className="wave-bar h-4" style={{ color: theme.primary_color }} />
                  <span className="wave-bar h-4" style={{ color: theme.primary_color, animationDelay: "0.15s" }} />
                  <span className="wave-bar h-4" style={{ color: theme.primary_color, animationDelay: "0.3s" }} />
                </div>
              </div>
            )}
          </div>

          <div className="p-3 border-t border-[#F5F2EB]/10 bg-[#1A1715] flex gap-2 items-center">
            <input value={text} disabled={sending || needsName}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              placeholder={needsName ? "Tell us your name above ↑" : "Type a message…"}
              data-testid="public-chat-input"
              className="flex-1 bg-transparent border-b border-[#F5F2EB]/15 font-mono text-sm px-2 py-2 focus:outline-none disabled:opacity-50"
            />
            <button onClick={send} disabled={!text.trim() || sending || needsName} data-testid="public-chat-send"
              style={{ background: theme.primary_color }}
              className="w-10 h-10 text-[#F5F2EB] flex items-center justify-center btn-press disabled:opacity-40"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.5} />}
            </button>
          </div>
          {!isEmbed && (
            <div className="px-4 py-2 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] border-t border-[#F5F2EB]/5">
              {agent?.show_branding ? (
                <>
                  Powered by{" "}
                  <a
                    href="https://agentreply.io"
                    target="_blank"
                    rel="noopener"
                    data-testid="public-chat-powered-by"
                    className="hover:underline"
                    style={{ color: theme.primary_color }}
                  >
                    AgentReply
                  </a>
                </>
              ) : (
                <>
                  Powered by <a href="/" className="hover:underline" style={{ color: theme.primary_color }}>{brandName}</a>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
